import * as React from 'react';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import { StripeProvider, CardNumberElement, CardExpiryElement, CardCVCElement, injectStripe, ReactStripeElements, Elements } from 'react-stripe-elements';
import { WithToast, withToast } from '../HOC/Toast';
import strings from 'src/strings';
import NumberFormat from 'react-number-format';
import { WithStyles, Theme, createStyles, withStyles, Button, CircularProgress, MenuItem, List, ListItem, ListItemText } from '@material-ui/core';
import { PaymentsAPI, APIError, APIErrorType } from 'src/api';
import { withUser, WithUser } from '../HOC/User';
import StripeElementWrapper from './StripeElementWrapper';
import classNames from 'classnames';

const NF: any = NumberFormat;

export interface PaymentFormProps {
    onNext: () => void
    onCancel?: () => void
    onBack?: () => void
    hideHeader?: boolean
    nextButtonText?: string
    className?: string
}

interface PaymentFormState {
    name: string;
    existing_id?: string;
    existing_cards: {
        id: string;
        brand: string;
        last4: string;
        name: string;
        exp: string;
    }[],
    errors: {
        name?: boolean,
    }, loading: boolean
}
class InjectedPaymentForm extends React.Component<WithToast & WithStyles<typeof styles> & WithUser & PaymentFormProps & ReactStripeElements.InjectedStripeProps, PaymentFormState> {

    constructor(props: WithToast & WithStyles<typeof styles> & PaymentFormProps & WithUser & ReactStripeElements.InjectedStripeProps) {
        super(props);
        this.state = {
            name: '',
            errors: {},
            loading: false,
            existing_cards: []
        }
    }

    componentDidMount() {
        this.setState({ loading: true })
        Promise.all([
            PaymentsAPI.getAllCreditCards(),
            PaymentsAPI.getCreditCard().catch(err => {
                return {
                    id: undefined
                }
            })
        ]).then(([all, my]) => {
            this.setState({ existing_cards: all, existing_id: my.id, loading: false })
        }).catch(() => {
            this.setState({ loading: false })
        })
    }

    private submit() {
        if (this.state.existing_id) {
            this.setState({ errors: {}, loading: true })
            PaymentsAPI.setDefaultCard(this.state.existing_id).then(() => {
                this.setState({ errors: {}, loading: false })
                this.props.onNext()
            }).catch(err => {
                this.props.showToast('error', strings.checkout_errorUsingCreditCard)
                this.setState({ errors: {}, loading: false })
            })
        } else {

            this.setState({ errors: {}, loading: true })
            this.props.stripe!.createToken(({
                name: this.state.name,
                type: 'card'
            } as any)).then((result) => {
                if (result.error) {
                    this.props.showToast('error', result.error.message!)
                    this.setState({ loading: false })
                } else {
                    PaymentsAPI.createCreditCard({
                        token: result.token!.id
                    }).then((card) => {
                        this.setState({ loading: false, existing_cards: this.state.existing_cards.concat(card), existing_id: card.id })
                        this.props.onNext()
                    }).catch((err: APIError) => {
                        if (err.type == APIErrorType.InvalidParameters) {
                            this.props.showToast('error', err.message)
                        } else {
                            this.props.showToast('error', strings.error_generic)
                        }
                        this.setState({ loading: false })
                    })
                }
            }).catch(err => {
                this.props.showToast('error', strings.error_generic)
                this.setState({ loading: false })
            })
        }
    }

    private back() {
        this.props.onBack && this.props.onBack()
    }

    private selectCard(id: string) {
        this.setState({ existing_id: id })
    }

    public render() {
        const { classes } = this.props
        return (
            <React.Fragment>
                {this.props.hideHeader !== true && <Typography variant="h6" gutterBottom>
                    {strings.checkout_paymentMethod}
                </Typography>}
                <div className={this.props.className}>
                    <List className={this.props.classes.list}>
                        {this.state.existing_cards.map((card, i) => {
                            return <ListItem button selected={this.state.existing_id == card.id} key={i} onClick={(e) => this.selectCard(card.id)}>
                                <ListItemText
                                    primary={card.name}
                                    secondary={
                                        <React.Fragment>
                                            {card.brand}<br />
                                            xxxx-xxxx-xxxx-{card.last4}, Exp: {card.exp}
                                        </React.Fragment>
                                    }
                                />
                            </ListItem>
                        })}
                    </List>
                    <form onSubmit={this.submit}>
                        <Grid container spacing={10}>
                            <Grid item xs={12} md={6}>
                                <TextField required id="cardName" label={strings.payment_nameOnCard} fullWidth error={this.state.errors.name}
                                    onChange={(e) => this.setState({ name: e.target.value, existing_id: undefined })} value={this.state.name}
                                    InputProps={{ autoComplete: "cc-name" }} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <StripeElementWrapper label={strings.payment_cardNumber} component={CardNumberElement} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <StripeElementWrapper label={strings.payment_expiryDate} component={CardExpiryElement} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <StripeElementWrapper label={strings.payment_cvv} component={CardCVCElement} />
                            </Grid>
                        </Grid>
                    </form>
                    <div className={classes.buttons}>
                        {this.props.onCancel && <Button
                            variant='outlined'
                            color="secondary"
                            onClick={this.props.onCancel.bind(this)}
                            disabled={this.state.loading}
                            className={this.props.classes.button + ' ' + this.props.classes.cancel_button}>
                            {strings.cancel}
                        </Button>}
                        {this.state.loading && <CircularProgress size={30} className={this.props.classes.button} />}
                        {this.props.onBack && <Button onClick={this.back.bind(this)} className={classes.button} disabled={this.state.loading}>
                            {strings.back}
                        </Button>}
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={this.state.loading}
                            onClick={this.submit.bind(this)}
                            className={classes.button}>
                            {this.props.nextButtonText || strings.checkout_next}
                        </Button>
                    </div>
                </div>
            </React.Fragment >
        )
    }
}

const styles = (theme: Theme) => createStyles({
    buttons: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    button: {
        marginTop: theme.spacing(3),
        marginLeft: theme.spacing(),
    },
    select: {
        minWidth: 80
    },
    cancel_button: {
        marginLeft: 0,
        marginRight: 'auto'
    },
    inline: {
        display: 'inline',
    },
    list: {
        paddingTop: 0,
        maxHeight: 200,
        position: 'relative',
        overflow: 'scroll',
    }
})
const Component = injectStripe(
    withUser(
        withStyles(styles)(
            withToast(InjectedPaymentForm)
        ), true
    ), { withRef: true }
) as React.ComponentClass<PaymentFormProps>
export default class PaymetForm extends React.Component<PaymentFormProps> {
    public render() {
        return (
            <Component {...this.props} />
        )
    }
}



// WEBPACK FOOTER //
// ./src/components/Checkout/PaymentForm.tsx